import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Drawer,
  Tooltip,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDashboardContext } from './DashboardContext';
import ChatComponent from './Components/ChatComponent';
import CloseIcon from '@mui/icons-material/Close';
import ExFacSummaries from './Components/ExFac/ExFacSummaries';
import dayjs from "dayjs";

function ExFacDashboard() {
  const {
    conversations,
    setConversation,
    chatIds,
    setChatId,
  } = useDashboardContext();

  const conversation = conversations.exFac || [];
  const currentChatId = chatIds.exFac;

  const [query, setQuery] = useState('');
  const [aiOverlayOpen, setAiOverlayOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [canUseChat, setCanUseChat] = useState(false);
  const chatWindowRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const loadingMessages = [
    'Analyzing your question...',
    'Gathering relevant information...',
    'Verifying details...'
  ];
  useEffect(() => { if (!conversation.length || !currentChatId) startNewChat(); }, []); // eslint-disable-line
  useEffect(() => { if (chatWindowRef.current) chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight; }, [conversation]);
  // const [currentLoadingMessage, setCurrentLoadingMessage] = useState(0);

  useEffect(() => {
    const permissionForChat = "can_use_exfac_chat"
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    if (permissions.includes(permissionForChat)) {
      setCanUseChat(true);
    } else {
      setCanUseChat(false);
    }
  });

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        // setCurrentLoadingMessage((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [loading, loadingMessages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  useEffect(() => {
    if (!conversation.length || !currentChatId) {
      startNewChat();
    }
  }, []); // eslint-disable-line

  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  };

  const startNewChat = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/start_new_chat_exfac`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      setChatId('exFac', data.thread_id);
      setConversation('exFac', [{
        role: 'assistant',
        content: 'Welcome to ExFac AI! Ask a question to uncover valuable insights into the ExFac data.',
        timestamp: Date.now()
      }]);
    } catch (error) {
      console.error('Error starting new chat:', error);
      setConversation('exFac', [{ role: 'assistant', content: 'Failed to start a new chat. Please try again later.' }]);
    }
  };

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    const userMessage = { role: 'user', content: searchQuery, timestamp: Date.now() };

    let updated = [];
    if (conversation.length === 1 && conversation[0]?.role === 'assistant') {
      updated = [userMessage];
    } else {
      updated = [...conversation, userMessage];
    }
    setConversation('exFac', updated);

    try {
      const res = await fetch(`${apiUrl}/api/ask_ex_fac`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: searchQuery }),
        credentials: 'include',
      });
      const data = await res.json();
      setFollowUpQuestions(data.follow_up?.split('\n').filter(Boolean) || []);

      if (data.answer?.headers && data.answer?.rows) {
        const tableMessage = {
          role: 'assistant',
          content: {
            type: 'table',
            headers: data.answer.headers,
            rows: data.answer.rows,
          },
          sql_query: data.sql_query,
          question: data.question,
          canDownload: data.can_download,
          timestamp: Date.now()
        };
        setConversation('exFac', [...updated, tableMessage]);
      } else {
        const assistantMessage = {
          role: 'assistant',
          content: `**RxAI:**\n${data.answer}`,
          sql_query: data.sql_query,
          question: data.question,
          canDownload: data.can_download,
          timestamp: Date.now()
        };
        setConversation('exFac', [...updated, assistantMessage]);
      }

      if (data.graph) {
        const graphMessage = {
          role: 'assistant',
          content: `**RxAI:**\n<h3>Graph Result</h3><img src="data:image/png;base64,${data.graph_image}" alt="Graph" style="max-width:100%;"/>`,
          timestamp: Date.now()
        };
        setConversation('exFac', [...updated, graphMessage]);
      }
    } catch (error) {
      setConversation('exFac', [...updated, { role: 'assistant', content: '**RxAI:**\nSorry, something went wrong. 🚫', timestamp: Date.now() }]);
    } finally {
      setLoading(false);
      setQuery('');
    }
  };

  const handleNewChat = () => {
    setConversation('exFac', []);
    setFollowUpQuestions([]);
    startNewChat();
  };

  const backgroundGradient = '#FFFFFF';

  // in ExFacDashboard()
  const handleOpenAI = (prefill) => {
    setAiOverlayOpen(true);
    if (prefill) setQuery(prefill); // put the suggested question into the input
  };

  return (
    <Box sx={{ padding: 3, background: backgroundGradient, minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'text.primary',letterSpacing: '0.5px' }}>
        Ex Fac AI Dashboard
      </Typography>

      <ExFacSummaries apiUrl={apiUrl} showAskRxAI={canUseChat} onAskRxAI={handleOpenAI} selectedDate={dayjs().format("YYYY-MM-DD")} />

      {/* ExFac AI Chat Overlay */}
      <Drawer
        anchor="right"
        open={aiOverlayOpen}
        onClose={() => setAiOverlayOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 700, md: 860, lg: 980 }, // wider
            maxWidth: '60vw',                                  // cap as 60% of screen
            borderLeft: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 2, height:'100%', display:'flex', flexDirection:'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>ExFac AI</Typography>
            <Box>
              <Tooltip title="Start New Chat">
                <IconButton onClick={handleNewChat} size="small" sx={{ mr: 0.5 }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton onClick={() => setAiOverlayOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            ref={chatWindowRef}
            sx={{ flex: 1, overflowY:'auto', border:'1px solid', borderColor:'divider', borderRadius: 1, p: 1, mt: 1 }}
          >
            {conversation.map((msg, index) => (
              <ChatComponent key={index} message={msg} />
            ))}
            {loading && <Typography variant="body2">Analyzing your question...</Typography>}
          </Box>

          <Box sx={{ display:'flex', gap: 1, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask a question…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && query.trim()) handleSearch(query); }}
            />
            <Button variant="contained" onClick={() => handleSearch(query)} disabled={!query.trim()}>
              Send
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default ExFacDashboard;